// shared-lib/group-customization/anchorTypes.js
//
// Which member of a fixture group is its ANCHOR — the object every link's
// anchor-local pose is expressed against, and the object that carries
// `groupCustomization`. Moved from the web editor so the server-side
// `resolveGroup` picks the same anchor the editor does; two lists drifting
// is exactly how links stamped on one object became invisible from another
// (#1502).

// Preferred anchor types, in priority order. The first type in the group
// that's also in this list becomes the anchor — a stable, "structural"
// reference point (cabinet/support box on the floor) instead of whatever the
// user happened to click.
const PREFERRED_ANCHOR_TYPES = [
  'lower-cabinet',
  // A scanned kitchen can yield a sink installation with no base cabinet.
  // Without the basin here the anchor fell through to whatever was clicked,
  // so the same group resolved differently from the sink than from the
  // faucet — and links stamped on one were invisible from the other. Kept
  // below 'lower-cabinet', which stays the preferred anchor when present.
  'kitchen-sink',
  'tub-support-box',
  'shower',
  'bathtub',
  'tub',
  'tub-shower',
  'toilet',
  'sink',
  'double-sink',
];

/**
 * The anchor of a group given its member objects: the first preferred type
 * present, else the member that already carries `groupCustomization`, else
 * the first member. Null for an empty list.
 */
/**
 * Does an object answer to an anchor type name? Matches the object's own
 * `type` OR its `category` map — producers tag a vanity cabinet as
 * `type: 'vanity-cabinet'` with `category: { 'lower-cabinet': true }`, and
 * the writers (getVanityCabinet) find it through the category. An exact-type
 * match here skipped that cabinet and anchored the group on its SINK, so the
 * 14 links stamped on the cabinet were invisible in the editor (2026-08-30).
 */
function matchesAnchorType(obj, typeName) {
  return (
    (obj?.type || '').toLowerCase() === typeName ||
    Boolean(obj?.category?.[typeName])
  );
}

/**
 * THE cabinet classifier — the same test the writers use (vanity-assembly's
 * `isLowerCabinet` delegates here). A bare `type:'cabinet'` body is a real
 * data shape (six live consumers test for it); when the picker didn't accept
 * it, links stamped on such a cabinet anchored invisibly (round-2 audit M1).
 */
function isLowerCabinetLike(o) {
  return (
    (o?.type || '').toLowerCase() === 'cabinet' ||
    matchesAnchorType(o, 'lower-cabinet')
  );
}

function pickAnchor(members) {
  if (!Array.isArray(members) || members.length === 0) return null;
  // THE STORED FACT wins (user-ratified 2026-08-31): whoever BUILDS a group
  // stamps `groupAnchor: true` on the body it writes links against, so
  // readers no longer re-derive the choice. The heuristic below serves
  // groups born before the stamp — and re-election when the anchor is gone.
  const stamped = members.find((o) => o?.groupAnchor);
  if (stamped) return stamped;
  for (const preferred of PREFERRED_ANCHOR_TYPES) {
    const matches =
      preferred === 'lower-cabinet'
        ? isLowerCabinetLike
        : (o) => matchesAnchorType(o, preferred);
    const match = members.find(matches);
    if (match) return match;
  }
  return members.find((o) => o?.groupCustomization) || members[0];
}

module.exports = {
  PREFERRED_ANCHOR_TYPES,
  matchesAnchorType,
  isLowerCabinetLike,
  pickAnchor,
};
